import abcLogo from "/abclabs-white.svg";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  PreJoin,
  RoomContext,
  VideoConference,
} from "@livekit/components-react";
import "@livekit/components-styles";
import "./App.css";
import { ErrorDialog } from "./components/ErrorDialog";
import {
  ExternalE2EEKeyProvider,
  RemoteParticipant,
  Room,
  RoomEvent,
  type E2EEOptions,
  type RoomOptions,
} from "livekit-client";
import { LanguageButton } from "./components/LanguageButton.tsx";
import { CustomControlBar } from "./components/CustomControlBar.tsx";
import { useTranslation } from "react-i18next";
import { WaitRoomPanel } from "./components/WaitRoomPanel.tsx";

const LIVEKIT_SERVER_URL = import.meta.env.VITE_LIVEKIT_SERVER_URL as string;
if (!LIVEKIT_SERVER_URL) throw new Error("VITE_LIVEKIT_SERVER_URL is required");

const ENABLE_RPC_NOTIFICATIONS =
  import.meta.env.VITE_ENABLE_RPC_NOTIFICATIONS === "true";

const CAREOS_CONNECT_API_URL = import.meta.env
  .VITE_CAREOS_CONNECT_API_URL as string;
if (!CAREOS_CONNECT_API_URL)
  throw new Error("VITE_CAREOS_CONNECT_API_URL is required");

const COLLECTOR_IDENTITY_PREFIX = "ABC_LABS_COLLECTOR";

type SessionState =
  | "prejoin"
  | "idle"
  | "joining"
  | "setup"
  | "active"
  | "error";

async function parseJsonSafe(resp: Response) {
  try {
    return await resp.json();
  } catch {
    return null;
  }
}

export default function App() {
  const { t } = useTranslation();

  const [urlSid, setUrlSid] = useState<string | null>(null);
  const [inputSid, setInputSid] = useState<string>("");
  const [isEditing, setIsEditing] = useState<boolean>(false);

  useEffect(() => {
    const readHash = () => {
      const params = new URLSearchParams(window.location.hash.slice(1));
      const sid = params.get("sid");
      setUrlSid(sid);
      if (sid) {
        setInputSid((prev) => (isEditing ? prev : sid));
        setIsEditing(false);
        setSessionState("prejoin");
      } else {
        setIsEditing(true);
        setInputSid("");
      }
    };
    readHash();
    window.addEventListener("hashchange", readHash);
    return () => window.removeEventListener("hashchange", readHash);
  }, [isEditing]);

  const [errorMessage, setErrorMessage] = useState<string>("");
  const [sessionState, setSessionState] = useState<SessionState>("idle");

  const [eventType, setEventType] = useState<string>("");
  const [sending, setSending] = useState<boolean>(false);
  const [sendStatus, setSendStatus] = useState<"" | "ok" | "error">("");

  const [pendingSid, setPendingSid] = useState<string | null>(null);
  const [lkToken, setLkToken] = useState<string | null>(null);

  const keyProviderRef = useRef<ExternalE2EEKeyProvider | null>(null);
  if (!keyProviderRef.current) {
    keyProviderRef.current = new ExternalE2EEKeyProvider();
  }

  const handleJoin = () => {
    setEventType("");
    const sidToUse =
      (inputSid && inputSid.trim()) || (urlSid && urlSid.trim()) || "";
    if (!sidToUse) return;
    window.location.hash = `sid=${encodeURIComponent(sidToUse)}`;
    setPendingSid(sidToUse);
    setSessionState("prejoin");
    setIsEditing(false);
  };

  useEffect(
    () => {
      if (sessionState !== "joining" || !pendingSid) return;
      let cancelled = false;
      const authEndpoint = `${CAREOS_CONNECT_API_URL}/video/auth/livekit/collector`;

      (async () => {
        try {
          const resp = await fetch(authEndpoint, {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({ sessionId: pendingSid }),
          });
          const data = await parseJsonSafe(resp);
          if (resp.ok) {
            if (!data?.token) {
              setErrorMessage(t("errors.tryAgainLater"));
              setSessionState("idle");
              return;
            }
            if (!cancelled) {
              setLkToken(data.token as string);
              setSessionState("setup");
            }
          } else {
            setErrorMessage(t("errors.tryAgainLater"));
            setSessionState("idle");
          }
        } catch (err) {
          console.error(err);
          setErrorMessage(t("errors.tryAgainLater"));
          setSessionState("idle");
        }
      })();

      return () => {
        cancelled = true;
      };
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [sessionState, pendingSid]
  );

  const room = useMemo(() => {
    const keyProvider = keyProviderRef.current!;
    const e2ee: E2EEOptions = {
      keyProvider,
      worker: new Worker(
        new URL("livekit-client/e2ee-worker", import.meta.url)
      ),
    };

    const roomOptions: RoomOptions = {
      e2ee,
    };

    return new Room(roomOptions);
  }, []);

  useEffect(() => {
    return () => {
      room.disconnect();
    };
  }, [room]);

  const [connected, setConnected] = useState(false);

  /** Called when the user presses "Join" in PreJoin */
  const handlePreJoinSubmit = useCallback(
    async (values: {
      username?: string;
      audioEnabled?: boolean;
      videoEnabled?: boolean;
      // these keys exist in components-react; we guard with optional chaining
      audioDeviceId?: string | null;
      videoDeviceId?: string | null;
      captureSettings?: {
        resolution?: { width: number; height: number; frameRate?: number };
      };
    }) => {
      if (!urlSid) {
        return;
      }
      setSessionState("joining");

      try {
        const resp = await fetch(
          `${CAREOS_CONNECT_API_URL}/video/auth/livekit/collector`,
          {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({ sessionId: urlSid }),
          }
        );
        if (resp.status === 404) {
          setErrorMessage(t("errors.noSuchSession", "Session not found"));
          setSessionState("prejoin");
          return;
        }
        const data = await resp.json().catch(() => null);
        if (!resp.ok || !data?.token) {
          setErrorMessage(t("errors.genericError"));
          setSessionState("prejoin");
          return
        }
        setLkToken(data.token);

        const e2eeKey = data.encryptionKey;
        const keyProvider = keyProviderRef.current;
        if (!keyProvider) {
          throw new Error("E2EE key provider not initialized");
        }

        await keyProvider.setKey(e2eeKey);
        await room.setE2EEEnabled(true);

        await room.connect(LIVEKIT_SERVER_URL, data.token, {
          autoSubscribe: true,
        });

        if (values.audioEnabled) {
          await room.localParticipant.setMicrophoneEnabled(true, {
            deviceId: values.audioDeviceId ?? "default",
          });
        } else {
          await room.localParticipant.setMicrophoneEnabled(false, {
            deviceId: values.audioDeviceId ?? "default",
          });
        }

        if (values.videoEnabled) {
          await room.localParticipant.setCameraEnabled(true, {
            deviceId: values.videoDeviceId ?? "default",
          });
        } else {
          await room.localParticipant.setCameraEnabled(false);
        }

        setConnected(true);
        setSessionState("active");
      } catch (e) {
        console.log(`Failed to connect: ${e}`);
        setSessionState("error");
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [urlSid, room]
  );

  const handleDisconnectParticipant = useCallback(
    async (identity: string) => {
      setDisconnectError(null);
      setDisconnectingId(identity);

      try {
        await room.localParticipant.performRpc({
          destinationIdentity: identity,
          method: "disconnect",
          payload: "", // no payload needed for now
          responseTimeout: 5000,
        });
        // The donor side should handle this RPC and disconnect itself.
      } catch (e) {
        console.error("Failed to send disconnect RPC", e);
        setDisconnectError(t("errors.tryAgainLater"));
      } finally {
        setDisconnectingId(null);
        setShowDisconnectPicker(false);
      }
    },
    [room, t]
  );

  useEffect(() => {
    const onDisconnected = () => {
      setConnected(false);
      setSessionState("prejoin");
      setLkToken(null);
      setPendingSid(null);
    };

    room.off(RoomEvent.Disconnected, onDisconnected);
    room.on(RoomEvent.Disconnected, onDisconnected);

    return () => {
      room.off(RoomEvent.Disconnected, onDisconnected);
    };
  }, [room]);

  const showNotification = useCallback(async () => {
    const msg = eventType.trim();
    if (!msg) return;

    setSending(true);
    setSendStatus("");

    try {
      const remoteParticipants = room.remoteParticipants;
      const donors = Array.from(remoteParticipants.values()).filter(
        (participant) =>
          !participant.identity.startsWith(COLLECTOR_IDENTITY_PREFIX)
      );
      if (donors.length === 0) {
        return;
      }
      const rpcPromises = donors.map((participant) =>
        room.localParticipant.performRpc({
          destinationIdentity: participant.identity,
          method: "showNotification",
          payload: msg,
          responseTimeout: 5000,
        })
      );

      const results = await Promise.allSettled(rpcPromises);
      const failures = results.filter((r) => r.status === "rejected");

      if (failures.length === 0) {
        setSendStatus("ok");
      } else if (failures.length === results.length) {
        setSendStatus("error");
      } else {
        // Partial success
        console.warn("Some notifications failed");
        setSendStatus("ok");
      }
      setEventType("");
    } finally {
      setSending(false);
    }
  }, [eventType, room]);

  const handleEventInputKey = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Enter" && !sending && eventType.trim()) {
      e.preventDefault();
      showNotification();
    }
  };

  const hasReadySid = !!urlSid && !isEditing;

  const [hasRemote, setHasRemote] = useState(false);
  const [donors, setDonors] = useState<RemoteParticipant[]>([]);
  const [showDisconnectPicker, setShowDisconnectPicker] = useState(false);
  const [disconnectingId, setDisconnectingId] = useState<string | null>(null);
  const [disconnectError, setDisconnectError] = useState<string | null>(null);

  useEffect(() => {
    if (!connected) {
      setHasRemote(false);
      setDonors([]);
      return;
    }

    const recompute = () => {
      const allRemotes = Array.from(room.remoteParticipants.values());
      const donorList = allRemotes.filter(
        (participant) =>
          !participant.identity.startsWith(COLLECTOR_IDENTITY_PREFIX)
      );
      setHasRemote(donorList.length > 0);
      setDonors(donorList);
    };

    recompute(); // initial

    const onJoin = () => recompute();
    const onLeave = () => recompute();

    room.on(RoomEvent.ParticipantConnected, onJoin);
    room.on(RoomEvent.ParticipantDisconnected, onLeave);

    return () => {
      room.off(RoomEvent.ParticipantConnected, onJoin);
      room.off(RoomEvent.ParticipantDisconnected, onLeave);
    };
  }, [connected, room]);

  useEffect(() => {
    if (sendStatus) {
      const timer = setTimeout(() => setSendStatus(""), 3000);
      return () => clearTimeout(timer);
    }
  }, [sendStatus]);

  return (
    <main className="relative">
      <div className="mx-auto w-full px-4 min-h-screen flex flex-col">
        {sessionState !== "active" && (
          <header
            id="page-header"
            className="flex items-end justify-center gap-x-10 mb-2"
          >
            <img src={abcLogo} alt="Abc Labs Logo" className="h-20 w-20" />
            <h1 className="hidden md:inline text-3xl font-light text-white -translate-y-2">
              {t("header.title")}
            </h1>

            <LanguageButton />
          </header>
        )}

        {sessionState === "idle" && (
          <div id="join-flow" className="card text-center mb-6">
            {hasReadySid ? (
              <div className="flex flex-col items-center gap-3 mt-3">
                <button
                  className="flex items-center justify-center gap-2 bg-purple-900/95 hover:bg-purple-950 text-white font-thin py-2 px-4 rounded disabled:opacity-50 shadow text-xl"
                  onClick={handleJoin}
                  disabled={!hasReadySid}
                  title={t("join.joinSession")}
                >
                  <img src="/call_icon.svg" alt="" className="h-5 w-5" />
                  <span>{t("join.joinSession")}</span>
                </button>
              </div>
            ) : (
              <div className="flex flex-col items-center gap-3">
                <input
                  type="text"
                  placeholder={t("join.enterSessionId")}
                  value={inputSid}
                  onChange={(e) => {
                    setInputSid(e.target.value);
                    setIsEditing(true);
                  }}
                  className="border border-gray-300 rounded px-3 py-2 w-64 text-center"
                />
                <div className="flex gap-2">
                  <button
                    className="bg-blue-600 hover:bg-blue-900 text-white font-bold py-2 px-4 rounded disabled:opacity-50"
                    onClick={handleJoin}
                    disabled={!inputSid.trim()}
                  >
                    {t("join.joinSession")}
                  </button>
                </div>
              </div>
            )}
          </div>
        )}

        {sessionState === "prejoin" && (
          <div className="px-4 h-[calc(100vh-100px)]" data-lk-theme="default">
            <div className="h-full grid place-items-center">
              <PreJoin
                onSubmit={handlePreJoinSubmit}
                defaults={{
                  audioEnabled: true,
                  videoEnabled: true,
                  username: "ABC Labs",
                }}
                joinLabel={t("join.joinSession")}
                micLabel={t("controls.microphone")}
                camLabel={t("controls.camera")}
              />
            </div>
          </div>
        )}

        {sessionState === "joining" && (
          <p className="text-center text-gray-600 mb-6">
            {t("join.connecting")}
          </p>
        )}

        {sessionState === "active" && connected && (
          <section
            id="session-section"
            className="w-full md:mt-8 flex flex-col items-center"
          >
            <div
              className="w-full h-[calc(100vh-100px)]"
              data-lk-theme="default"
            >
              {lkToken ? (
                <RoomContext.Provider value={room}>
                  <div className="flex h-full flex-col">
                    {/* Top bar above the video conference */}
                    {donors.length > 0 && (
                      <div className="px-3 pt-3 flex justify-end">
                        <div className="relative">
                          <button
                            type="button"
                            onClick={() => {
                              setShowDisconnectPicker((prev) => !prev);
                              setDisconnectError(null);
                            }}
                            className="inline-flex items-center gap-2 rounded bg-white-5 hover:bg-black/50 text-white text-xs px-3 py-1 shadow"
                          >
                            {t(
                              "controls.participants",
                              "Participants"
                            )}
                            <span className="text-xs opacity-80">
                              ({donors.length})
                            </span>
                          </button>

                          {showDisconnectPicker && (
                            <div className="absolute right-0 mt-2 w-72 rounded bg-black/90 border border-white/10 shadow-lg z-20">
                              <div className="px-3 py-2 border-b border-white/10 flex items-center justify-between">
                                <span className="text-xs text-white/70">
                                  {t(
                                    "controls.selectParticipantToDisconnect",
                                    "Select participant to disconnect"
                                  )}
                                </span>
                                <button
                                  type="button"
                                  onClick={() => setShowDisconnectPicker(false)}
                                  className="text-xs text-white/60 hover:text-white"
                                  title={t("controls.close", "Close")}
                                >
                                  ✕
                                </button>
                              </div>

                              <ul className="max-h-64 overflow-y-auto">
                                {donors.map((p) => (
                                  <li
                                    key={p.sid}
                                    className="flex items-center justify-between px-3 py-2 text-sm text-white hover:bg-white/10"
                                  >
                                    <div className="flex flex-col">
                                      <span className="font-medium">
                                        {p.name || p.identity}
                                      </span>
                                      <span className="text-[11px] text-white/50 font-mono">
                                        {p.identity}
                                      </span>
                                    </div>

                                    <button
                                      type="button"
                                      onClick={() =>
                                        handleDisconnectParticipant(p.identity)
                                      }
                                      disabled={disconnectingId === p.identity}
                                      className="ml-3 rounded bg-red-600 hover:bg-red-700 text-xs text-white px-3 py-1 disabled:opacity-60"
                                    >
                                      {disconnectingId === p.identity
                                        ? t(
                                            "controls.disconnecting",
                                            "Disconnecting…"
                                          )
                                        : t(
                                            "controls.disconnect",
                                            "Disconnect"
                                          )}
                                    </button>
                                  </li>
                                ))}
                              </ul>

                              {disconnectError && (
                                <div className="px-3 py-2 text-xs text-red-300 border-t border-white/10">
                                  {disconnectError}
                                </div>
                              )}
                            </div>
                          )}
                        </div>
                      </div>
                    )}

                    {/* Video conference area */}
                    <div className="flex-1 min-h-0 grid items-stretch gap-3 p-3">
                      <div className="abc-room min-w-0 min-h-0">
                        <VideoConference />
                      </div>
                    </div>

                    <div className="px-3 pb-3">
                      <CustomControlBar enableScreenShare={true} />
                    </div>
                  </div>
                </RoomContext.Provider>
              ) : (
                <div />
              )}
            </div>
          </section>
        )}

        {ENABLE_RPC_NOTIFICATIONS &&
          sessionState === "active" &&
          connected &&
          hasRemote && (
            <div className="fixed bottom-5 left-1/2 -translate-x-1/2 w-full max-w-[500px] px-4">
              <div className="flex items-center gap-2 py-3">
                <input
                  id="notification-input"
                  type="text"
                  value={eventType}
                  onChange={(e) => setEventType(e.target.value)}
                  onKeyDown={handleEventInputKey}
                  placeholder={t("controls.notificationPlaceholder")}
                  className="flex-1 min-w-0 border rounded px-3 py-2"
                />
                <button
                  onClick={showNotification}
                  disabled={sending || !eventType.trim()}
                  className="bg-purple-950 text-white rounded px-4 py-2 disabled:opacity-30"
                  title={t("controls.notify")}
                >
                  {sending ? t("controls.sending") : t("controls.notify")}
                </button>
                {sendStatus === "ok" && (
                  <span className="text-purple-950 text-xl">✓</span>
                )}{" "}
              </div>
            </div>
          )}
        {sessionState === "active" && connected && (
          <WaitRoomPanel
            sessionId={urlSid ?? ""}
            apiBaseUrl={CAREOS_CONNECT_API_URL}
            onError={setErrorMessage}
          />
        )}
        <ErrorDialog
          message={errorMessage}
          onClose={useCallback(() => setErrorMessage(""), [])}
        />
      </div>
    </main>
  );
}
